import { getLogger } from "rcd-cpp-logger";
import { useDispatch } from "react-redux";

const logger = getLogger("webSocketActions");
import { ThunkDispatch } from "redux-thunk";
import { ContentPlanRootState } from "../elements/contentPlan/store";
import { ElementAction } from "../elements/elementAction";
import { Functions } from "../types/types";
import { Metadata } from "../types/Metadata";
import { WebSocketManager } from "./WebSocketManager";
import { updateMetadataFormState } from "../components/MetadataForm/metadataFormReducer";
import { getDirtyFieldKeys } from "../utils/fieldUtils";
import { WSMsgType } from "../enums/WSMsgType";

type S = ContentPlanRootState;

type PayloadType = {
  data: Partial<Metadata>[];
};
interface WSMessage {
  type: WSMsgType;
  payload?: string | PayloadType;
}

const isWSMessage = (payload: unknown): payload is WSMessage => {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return typeof p.type === "string" && (Object.values(WSMsgType) as string[]).includes(p.type);
};

const handleWebSocketMessage =
  (message: unknown): ElementAction<Functions, S> =>
  (dispatch, getState) => {
    const state = getState();
    const currentRefId = state.form?.data?.props?.refId;
    if (!currentRefId) {
      logger.debug("Ignoring WS message — no form open");
      return;
    }

    if (!isWSMessage(message)) {
      logger.warn("WS message has unknown or invalid type, ignoring", message);
      return;
    }

    if (message.type !== WSMsgType.DataDetails) {
      logger.debug(`Ignoring WS message of type ${message.type} — no handler`);
      return;
    }

    const parsedPayload: PayloadType =
      typeof message.payload === "string" ? JSON.parse(message.payload) : message.payload;
    const incomingData = parsedPayload?.data?.[0];
    if (!incomingData) {
      logger.warn("WS message contained no data");
      return;
    }

    const incomingRefId = incomingData?.props?.refId;
    if (incomingRefId !== currentRefId) {
      logger.debug(
        `Ignoring stale WS message — incoming: ${incomingRefId}, current: ${currentRefId}`,
      );
      return;
    }

    logger.info(`Applying WS update for refId: ${currentRefId}`);

    const { data: currentData, original: currentOriginal, schema } = state.form;
    const newOriginal: Partial<Metadata> = JSON.parse(JSON.stringify(incomingData));

    const mergedProps = { ...incomingData.props };
    if (currentData?.props && currentOriginal?.props && schema) {
      const dirtyKeys = getDirtyFieldKeys(schema, currentData, currentOriginal);
      if (dirtyKeys.size > 0) {
        logger.debug(`Preserving ${dirtyKeys.size} dirty field(s) for refId: ${currentRefId}`);
        const currentProps = currentData.props;
        dirtyKeys.forEach((key) => {
          mergedProps[key] = currentProps[key];
        });
      }
    }

    dispatch(
      updateMetadataFormState({
        data: { ...incomingData, props: mergedProps },
        original: newOriginal,
      }),
    );
  };

const initWebSocket = (): ElementAction<Functions, S> => async (dispatch) => {
  const manager = WebSocketManager.getInstance();
  if (!manager.getBaseUrl()) {
    logger.debug("WebSockets disabled — skipping init");
    return;
  }
  manager.onMessage((message) => {
    dispatch(handleWebSocketMessage(message));
  });
  await manager.connect();
};

const subscribeToData =
  (refId: string): ElementAction<Functions, S> =>
  () => {
    const manager = WebSocketManager.getInstance();
    if (!manager.getBaseUrl()) {
      logger.debug(`WebSockets disabled — skipping subscribe for refId: ${refId}`);
      return;
    }
    manager.subscribe(refId, WSMsgType.DataDetails);
  };

const unsubscribeFromData =
  (refId: string): ElementAction<Functions, S> =>
  () => {
    WebSocketManager.getInstance().unsubscribe(refId);
  };

const disconnectWebSocket = (): ElementAction<Functions, S> => () => {
  const manager = WebSocketManager.getInstance();
  manager.removeMessageHandler();
  manager.disconnect();
};

export const useWebSocketActions = () => {
  const dispatch: ThunkDispatch<S, any, any> = useDispatch();
  return {
    initWebSocket: () => dispatch(initWebSocket()),
    subscribeToData: (refId: string) => dispatch(subscribeToData(refId)),
    unsubscribeFromData: (refId: string) => dispatch(unsubscribeFromData(refId)),
    disconnectWebSocket: () => dispatch(disconnectWebSocket()),
  };
};
